import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Pencil, Sparkles, Loader2, Plus, Trash2, ShieldAlert } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { succLabel } from '@/lib/successionLabels';
import { useSuccGeneration } from '@/hooks/useSuccGeneration';

const QUADRANTS = ['technical', 'education', 'experience', 'critical_experiences'];

export const SuccIdealTab = ({ position, isES, reload }) => {
  const x = (es, en) => (isES ? es : en);
  const quads = (position.ideal_scenario || {}).quadrants || {};
  const [edit, setEdit] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const { gen, start: generate } = useSuccGeneration(position.id, 'ideal', isES, reload);

  const openEdit = (q) => {
    setEdit(q);
    setRows(((quads[q] || {}).requirements || []).map((r) => ({ ...r })));
  };
  const total = rows.reduce((a, r) => a + (parseInt(r.weight, 10) || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/client/succession/positions/${position.id}/ideal/${edit}`, { requirements: rows });
      toast.success(x('Cuadrante confirmado.', 'Quadrant confirmed.'));
      setEdit(null); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail === 'succ_weights_must_total_100'
        ? x('Los pesos del cuadrante deben sumar 100%.', 'Quadrant weights must total 100%.')
        : formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="succ-ideal-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold">{x('Escenario ideal del futuro titular', 'Ideal future holder scenario')}</h3>
          <p className="text-xs text-muted-foreground">{x('Perfil recomendado para ocupar exitosamente esta posición — estándar de preparación, no la descripción formal del puesto.', 'Recommended profile to successfully hold this position — a readiness standard, not the formal job description.')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={generate} disabled={gen} className="gap-1.5" data-testid="succ-ideal-generate">
          {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {gen ? x('Generando borrador…', 'Generating draft…') : x('Generar automáticamente', 'Generate automatically')}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {QUADRANTS.map((q) => {
          const qd = quads[q] || {};
          const reqs = qd.requirements || [];
          return (
            <Card key={q} className="p-4 shadow-card" data-testid={`succ-quadrant-${q}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="text-sm font-semibold">{succLabel(isES, 'quadrant', q)}</h4>
                <div className="flex items-center gap-1.5">
                  {qd.status === 'draft' && (
                    <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-[10px]">{x('Borrador', 'Draft')}</Badge>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openEdit(q)} data-testid={`succ-quadrant-edit-${q}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              {reqs.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">{x('Sin requisitos definidos.', 'No requirements defined.')}</p>
              ) : reqs.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-2 py-1.5 border-b last:border-0 text-sm">
                  <div className="min-w-0">
                    <p className="leading-snug">{r.name}
                      {r.mandatory && <ShieldAlert className="inline h-3.5 w-3.5 text-destructive ml-1 -mt-0.5" />}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{succLabel(isES, 'importance', r.importance)}{r.mandatory ? ` · ${x('Habilitante', 'Enabling')}` : ''}</p>
                  </div>
                  <span className="text-xs font-semibold tabular-nums shrink-0">{r.weight}%</span>
                </div>
              ))}
            </Card>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-[1000px] max-h-[85vh] overflow-y-auto" data-testid="succ-ideal-dialog">
          <DialogHeader><DialogTitle>{edit ? succLabel(isES, 'quadrant', edit) : ''}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="hidden sm:flex items-center gap-2 px-0.5 text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
              <span className="flex-1">{x('Requisito', 'Requirement')}</span>
              <span className="w-[140px] shrink-0">{x('Clasificación', 'Classification')}</span>
              <span className="w-[110px] shrink-0">{x('Habilitante', 'Enabling')}</span>
              <span className="w-[100px] shrink-0">{x('Peso %', 'Weight %')}</span>
              <span className="w-[40px] shrink-0" />
            </div>
            {rows.map((r, i) => (
              <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                <Input className="flex-1 min-w-[200px]" placeholder={x('Requisito', 'Requirement')} value={r.name || ''}
                  onChange={(e) => setRows(rows.map((rr, j) => (j === i ? { ...rr, name: e.target.value } : rr)))} data-testid={`succ-req-name-${i}`} />
                <div className="w-[140px] shrink-0">
                  <Select value={r.importance || 'desirable'} onValueChange={(v) => setRows(rows.map((rr, j) => (j === i ? { ...rr, importance: v } : rr)))}>
                    <SelectTrigger className="h-9" data-testid={`succ-req-importance-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="essential">{x('Esencial', 'Essential')}</SelectItem>
                      <SelectItem value="desirable">{x('Deseable', 'Desirable')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[110px] shrink-0 flex items-center gap-1.5">
                  <Checkbox checked={!!r.mandatory} onCheckedChange={(v) => setRows(rows.map((rr, j) => (j === i ? { ...rr, mandatory: !!v } : rr)))} data-testid={`succ-req-mandatory-${i}`} />
                  <span className="text-[11px] text-muted-foreground leading-tight">{x('Habilitante', 'Enabling')}</span>
                </div>
                <div className="w-[100px] shrink-0 relative">
                  <Input className="pr-7 text-right tabular-nums" type="number" min="0" max="100" value={r.weight ?? ''}
                    onChange={(e) => setRows(rows.map((rr, j) => (j === i ? { ...rr, weight: e.target.value } : rr)))} data-testid={`succ-req-weight-${i}`} />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">%</span>
                </div>
                <Button variant="ghost" size="sm" className="w-[40px] shrink-0 h-9 px-0" data-testid={`succ-req-delete-${i}`}
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              <Button variant="outline" size="sm" className="gap-1" onClick={() => setRows([...rows, { name: '', importance: 'desirable', mandatory: false, weight: 0 }])} data-testid="succ-req-add">
                <Plus className="h-3.5 w-3.5" /> {x('Agregar requisito', 'Add requirement')}
              </Button>
              <span className={`text-sm font-semibold ${total === 100 || rows.length === 0 ? 'text-success' : 'text-destructive'}`} data-testid="succ-weights-total">
                {x('Total', 'Total')}: {total}%
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>{x('Cancelar', 'Cancel')}</Button>
            <Button onClick={save} disabled={busy || (rows.length > 0 && total !== 100)} className="bg-gradient-primary hover:opacity-90" data-testid="succ-ideal-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : x('Guardar y confirmar', 'Save and confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
